import { Component, EventEmitter, Input, Output } from '@angular/core';

// Presentational step indicator: a row of dots joined by connectors. Dot states:
//  - filled green : a visited step (the user has been there) — includes current.
//  - green ring   : the next step, once the current step has enough data (ready
//                   to advance). Clicking it advances and it becomes filled.
//  - red ring     : a locked future step the user cannot reach yet.
// Clicking any green dot (filled or ring) navigates/advances there.
@Component({
  standalone: false,
  selector: 'wizard-stepper',
  templateUrl: './wizard-stepper.component.html',
  styleUrls: ['./wizard-stepper.component.scss']
})
export class WizardStepperComponent {
  @Input() totalSteps = 5;
  @Input() currentStep = 1;
  @Input() furthestStepReached = 1;
  // Whether the current step has enough data to advance (lights the next dot's ring green).
  @Input() canGoForward = false;

  @Output() stepClicked = new EventEmitter<number>();

  get steps(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i + 1);
  }

  // Visited steps are filled green (become filled only once the user lands there).
  isFilled(step: number): boolean {
    return step <= this.furthestStepReached;
  }

  // The next, not-yet-visited step shows a green ring once the current step is valid.
  isReady(step: number): boolean {
    return step === this.currentStep + 1 && step > this.furthestStepReached && this.canGoForward;
  }

  isClickable(step: number): boolean {
    return this.isFilled(step) || this.isReady(step);
  }

  isCurrent(step: number): boolean {
    return step === this.currentStep;
  }

  // The connector leading into a dot is filled once that dot's step is reached.
  isConnectorFilled(step: number): boolean {
    return step <= this.currentStep;
  }

  onClick(step: number): void {
    if (this.isClickable(step)) {
      this.stepClicked.emit(step);
    }
  }
}
